import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import {
  Search as SearchIcon,
  Download,
  Database,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { supabase, Entry } from '../lib/supabase';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import * as XLSX from 'xlsx';
import "react-datepicker/dist/react-datepicker.css";

interface Filters {
  dateRange: [Date | null, Date | null];
  textSearch: string;
  farmerId: string | null;
  customerId: string | null;
  invoiced: 'all' | 'yes' | 'no';
  globalgap: 'all' | 'yes' | 'no' | 'unset';
  archived: 'all' | 'yes' | 'no';
  quantityMin: string;
  quantityMax: string;
}

const columnHelper = createColumnHelper<Entry>();

export default function Search() {
  const navigate = useNavigate();
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [filters, setFilters] = useState<Filters>({
    dateRange: [null, null],
    textSearch: '',
    farmerId: null,
    customerId: null,
    invoiced: 'all',
    globalgap: 'all',
    archived: 'all',
    quantityMin: '',
    quantityMax: '',
  });
  const [results, setResults] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [farmers, setFarmers] = useState<{ id: string; name: string }[]>([]);
  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);

  const [startDate, endDate] = filters.dateRange;

  useEffect(() => {
    async function loadOptions() {
      const [farmersRes, customersRes] = await Promise.all([
        supabase.from('farmers').select('id, name').order('name'),
        supabase.from('customers').select('id, name').order('name'),
      ]);
      if (farmersRes.data) setFarmers(farmersRes.data);
      if (customersRes.data) setCustomers(customersRes.data);
    }
    loadOptions();
  }, []);

  function applySharedFilters(q: any) {
    if (startDate) q = q.gte('date', format(startDate, 'yyyy-MM-dd'));
    if (endDate) q = q.lte('date', format(endDate, 'yyyy-MM-dd'));
    if (filters.farmerId) q = q.eq('farmer_id', filters.farmerId);
    if (filters.customerId) q = q.eq('customer_id', filters.customerId);
    if (filters.invoiced !== 'all') q = q.eq('invoiced', filters.invoiced === 'yes');
    if (filters.globalgap !== 'all') {
      if (filters.globalgap === 'unset') {
        q = q.is('globalgap_confirmed', null);
      } else {
        q = q.eq('globalgap_confirmed', filters.globalgap === 'yes' ? 'YES' : 'NO');
      }
    }
    if (filters.archived !== 'all') q = q.eq('archived', filters.archived === 'yes');
    return q;
  }

  async function handleSearch() {
    try {
      setLoading(true);
      setHasSearched(true);

      const textSearch = filters.textSearch.trim();
      const hasTextSearch = textSearch.length > 0;
      let allResults: Entry[] = [];

      if (hasTextSearch) {
        let query1 = supabase
          .from('entries')
          .select(`
            *,
            farmer:farmers(name),
            customer:customers(name),
            products(description, crates, pallets)
          `);
        let query2 = supabase
          .from('entries')
          .select(`
            *,
            farmer:farmers(name),
            customer:customers(name),
            products!inner(description, crates, pallets)
          `);

        query1 = applySharedFilters(query1);
        query2 = applySharedFilters(query2);

        query1 = query1.or(`delivery_note_number.ilike.%${textSearch}%,notes.ilike.%${textSearch}%`);
        query2 = query2.ilike('products.description', `%${textSearch}%`);

        const [{ data: data1, error: error1 }, { data: data2, error: error2 }] = await Promise.all([
          query1.order('date', { ascending: true }),
          query2.order('date', { ascending: true }),
        ]);

        if (error1) throw error1;
        if (error2) throw error2;

        const merged = [...(data1 || []), ...(data2 || [])];
        allResults = Array.from(new Map(merged.map(item => [item.id, item])).values());
      } else {
        let query = supabase
          .from('entries')
          .select(`
            *,
            farmer:farmers(name),
            customer:customers(name),
            products(description, crates, pallets)
          `);
        query = applySharedFilters(query);
        const { data, error } = await query.order('date', { ascending: true });
        if (error) throw error;
        allResults = data || [];
      }

      const hasQuantityFilter = filters.quantityMin || filters.quantityMax;
      if (hasQuantityFilter) {
        const min = filters.quantityMin ? parseFloat(filters.quantityMin) : -Infinity;
        const max = filters.quantityMax ? parseFloat(filters.quantityMax) : Infinity;
        allResults = allResults.filter(entry =>
          entry.products?.some((p: any) =>
            (p.crates >= min && p.crates <= max) ||
            (p.pallets >= min && p.pallets <= max)
          )
        );
      }

      setResults(allResults);
    } catch (error) {
      console.error('Error searching entries:', error);
      toast.error('Failed to search entries');
    } finally {
      setLoading(false);
    }
  }

  function clearFilters() {
    setFilters({
      dateRange: [null, null],
      textSearch: '',
      farmerId: null,
      customerId: null,
      invoiced: 'all',
      globalgap: 'all',
      archived: 'all',
      quantityMin: '',
      quantityMax: '',
    });
    setResults([]);
    setHasSearched(false);
  }

  function handleRowClick(date: string) {
    navigate(`/calendar?date=${date}`);
  }

  function buildExportWorkbook(data: Entry[]) {
    const wb = XLSX.utils.book_new();

    const exportData = data.map(entry => ({
      'Date': format(new Date(entry.date), 'yyyy-MM-dd'),
      'Delivery Note': entry.delivery_note_number || '',
      'Farmer': entry.farmer?.name || '',
      'Customer': entry.customer?.name || '',
      'Invoiced': entry.invoiced ? 'Yes' : 'No',
      'GlobalGap Confirmed': entry.globalgap_confirmed || '—',
      'Notes': entry.notes || '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    const productsData = data.flatMap(entry =>
      (entry.products || []).map((product: any) => ({
        'Date': format(new Date(entry.date), 'yyyy-MM-dd'),
        'Delivery Note': entry.delivery_note_number || '',
        'Farmer': entry.farmer?.name || '',
        'Customer': entry.customer?.name || '',
        'Invoiced': entry.invoiced ? 'Yes' : 'No',
        'GlobalGap Confirmed': entry.globalgap_confirmed || '—',
        'Description': product.description,
        'Crates': product.crates,
        'Pallets': product.pallets || 0,
      }))
    );
    const productsWs = XLSX.utils.json_to_sheet(productsData);

    XLSX.utils.book_append_sheet(wb, ws, "Entries");
    XLSX.utils.book_append_sheet(wb, productsWs, "Products");

    return wb;
  }

  function handleExport() {
    if (!results.length) return;
    try {
      const wb = buildExportWorkbook(results);
      const datePart = startDate && endDate
        ? `${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}`
        : format(new Date(), 'yyyy-MM-dd');
      XLSX.writeFile(wb, `entries-${datePart}.xlsx`);
      toast.success('Entries exported successfully');
    } catch (error) {
      console.error('Error exporting entries:', error);
      toast.error('Failed to export entries');
    }
  }

  async function handleExportAll() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('entries')
        .select(`
          *,
          farmer:farmers(name),
          customer:customers(name),
          products(description, crates, pallets)
        `)
        .order('date', { ascending: true });

      if (error) throw error;
      if (!data?.length) {
        toast.error('No entries found');
        return;
      }

      const wb = buildExportWorkbook(data);
      XLSX.writeFile(wb, `all-entries-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      toast.success('All entries exported successfully');
    } catch (error) {
      console.error('Error exporting all entries:', error);
      toast.error('Failed to export all entries');
    } finally {
      setLoading(false);
    }
  }

  const columns = useMemo(() => [
    columnHelper.accessor('date', {
      header: 'Date',
      cell: info => format(new Date(info.getValue()), 'yyyy-MM-dd'),
    }),
    columnHelper.accessor('delivery_note_number', {
      header: 'Delivery Note',
      cell: info => info.getValue() || 'No DN',
    }),
    columnHelper.accessor(row => row.farmer?.name, {
      id: 'farmer',
      header: 'Farmer',
      cell: info => info.getValue() || '—',
    }),
    columnHelper.accessor(row => row.customer?.name, {
      id: 'customer',
      header: 'Customer',
      cell: info => info.getValue() || '—',
    }),
    columnHelper.accessor('invoiced', {
      header: 'Invoiced',
      cell: info => (
        <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${info.getValue() ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
          {info.getValue() ? 'Yes' : 'No'}
        </span>
      ),
    }),
    columnHelper.accessor('globalgap_confirmed', {
      header: 'GlobalGap',
      cell: info => {
        const val = info.getValue();
        return (
          <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
            val === 'YES' ? 'bg-green-100 text-green-800' :
            val === 'NO' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-600'
          }`}>
            {val || '—'}
          </span>
        );
      },
    }),
    columnHelper.accessor(row => row.products, {
      id: 'products',
      header: 'Products',
      cell: info => {
        const products = info.getValue() || [];
        return (
          <ul className="list-disc list-inside text-sm">
            {products.map((product: any, index: number) => (
              <li key={index}>
                {product.description} - {product.crates} crates
                {product.pallets > 0 && `, ${product.pallets} pallets`}
              </li>
            ))}
          </ul>
        );
      },
    }),
    columnHelper.accessor('notes', {
      header: 'Notes',
      cell: info => info.getValue() || '—',
    }),
  ], []);

  const table = useReactTable({
    data: results,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const farmerOption = filters.farmerId
    ? { value: filters.farmerId, label: farmers.find(f => f.id === filters.farmerId)?.name || '' }
    : null;

  const customerOption = filters.customerId
    ? { value: filters.customerId, label: customers.find(c => c.id === filters.customerId)?.name || '' }
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Search Entries</h1>

        <div className="mb-6 border rounded-lg border-gray-200">
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-t-lg"
          >
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="font-medium text-gray-700">Filters</span>
            </div>
            {filtersExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {filtersExpanded && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Range
                  </label>
                  <DatePicker
                    selectsRange={true}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => setFilters({ ...filters, dateRange: update as [Date | null, Date | null] })}
                    dateFormat="yyyy-MM-dd"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholderText="Select date range..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Text Search
                  </label>
                  <input
                    type="text"
                    value={filters.textSearch}
                    onChange={(e) => setFilters({ ...filters, textSearch: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Search delivery note, notes, products..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Farmer
                  </label>
                  <Select
                    isClearable
                    options={farmers.map(f => ({ value: f.id, label: f.name }))}
                    value={farmerOption}
                    onChange={(option) => setFilters({ ...filters, farmerId: option?.value || null })}
                    className="text-sm"
                    placeholder="All farmers..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                  </label>
                  <Select
                    isClearable
                    options={customers.map(c => ({ value: c.id, label: c.name }))}
                    value={customerOption}
                    onChange={(option) => setFilters({ ...filters, customerId: option?.value || null })}
                    className="text-sm"
                    placeholder="All customers..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoiced
                  </label>
                  <select
                    value={filters.invoiced}
                    onChange={(e) => setFilters({ ...filters, invoiced: e.target.value as Filters['invoiced'] })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="all">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GlobalGap
                  </label>
                  <select
                    value={filters.globalgap}
                    onChange={(e) => setFilters({ ...filters, globalgap: e.target.value as Filters['globalgap'] })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="all">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unset">Unset</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Archived
                  </label>
                  <select
                    value={filters.archived}
                    onChange={(e) => setFilters({ ...filters, archived: e.target.value as Filters['archived'] })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="all">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity Range (Crates or Pallets)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      value={filters.quantityMin}
                      onChange={(e) => setFilters({ ...filters, quantityMin: e.target.value })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      value={filters.quantityMax}
                      onChange={(e) => setFilters({ ...filters, quantityMax: e.target.value })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Max"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-4 py-2 bg-[#4a3f35] text-white rounded-md hover:bg-[#3a2f25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4a3f35] disabled:opacity-50 flex items-center space-x-2"
                >
                  <SearchIcon className="h-5 w-5" />
                  <span>{loading ? 'Searching...' : 'Search'}</span>
                </button>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center space-x-2"
                >
                  <X className="h-5 w-5" />
                  <span>Clear All Filters</span>
                </button>
                {results.length > 0 && (
                  <button
                    onClick={handleExport}
                    className="px-4 py-2 bg-[#8cc63f] text-white rounded-md hover:bg-[#7cb62f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8cc63f] flex items-center space-x-2"
                  >
                    <Download className="h-5 w-5" />
                    <span>Export Results</span>
                  </button>
                )}
                <button
                  onClick={handleExportAll}
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center space-x-2"
                >
                  <Database className="h-5 w-5" />
                  <span>Export All</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {hasSearched && !loading && results.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg font-medium">No results found</p>
            <p className="text-sm mt-1">Try adjusting your filters or date range</p>
          </div>
        )}

        {!hasSearched && !loading && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg font-medium">Ready to search</p>
            <p className="text-sm mt-1">Apply filters and click Search to see results</p>
          </div>
        )}

        {results.length > 0 && (
          <>
            <div className="mb-2 text-sm text-gray-600">
              Showing {results.length} result{results.length !== 1 ? 's' : ''}
            </div>

            {/* Mobile: card list */}
            <div className="md:hidden space-y-3">
              {table.getRowModel().rows.map((row) => (
                <div
                  key={row.id}
                  onClick={() => handleRowClick(row.original.date)}
                  className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-sm">
                      {format(new Date(row.original.date), 'MMM d, yyyy')}
                    </span>
                    <span className={clsx('text-xs px-2 py-0.5 rounded-full',
                      row.original.invoiced ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    )}>
                      {row.original.invoiced ? 'Invoiced' : 'Open'}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-700">
                    DN: {row.original.delivery_note_number || '—'}
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    {row.original.farmer?.name} → {row.original.customer?.name}
                  </div>
                  {row.original.products?.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {row.original.products.map((p: any, i: number) => (
                        <div key={i} className="text-xs text-gray-600">
                          {p.description}: {p.crates} crates
                          {p.pallets > 0 && `, ${p.pallets} pallets`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop: existing table */}
            <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
                      >
                        <div className="flex items-center space-x-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === 'asc' && <span>▲</span>}
                          {header.column.getIsSorted() === 'desc' && <span>▼</span>}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(row.original.date)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4 text-sm text-gray-900">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
